// eslint-disable-next-line no-unused-vars
import { circle } from '@turf/turf';
import { useMotionValue, useSpring } from 'framer-motion';
import 'mapbox-gl/dist/mapbox-gl.css';
import React from 'react';
import { Layer, Map, Marker, NavigationControl, Source } from 'react-map-gl';

import { Card, Slider, Typography } from '@growdash/design-system/atoms';
import { Car, MapPin } from 'lucide-react';

// ----------------------------------------------------------------------

type CoverageMapProps = {
  ltd: number;
  lng: number;
  deliveryAreas?: string[];
};

// ----------------------------------------------------------------------

export default function CoverageMap({
  ltd = 0,
  lng = 0,
  deliveryAreas,
}: CoverageMapProps) {
  const defaultRadius = 5;
  const [currentView] = React.useState('map');

  const rad = useMotionValue(defaultRadius);
  const radiusSpring = useSpring(rad, {
    stiffness: 500,
    damping: 100,
    mass: 5,
  });

  const options = {
    steps: 36,
    units: 'kilometers',
  };
  const [scaledCircle, setScaledCircle] = React.useState(
    circle([lng, ltd], radiusSpring.get(), options as any),
  );

  React.useEffect(() => {
    const unsubscribe = radiusSpring.onChange((value) => {
      const newCircle = circle([lng, ltd], value, {
        steps: 36,
        units: 'kilometers',
      });
      setScaledCircle(newCircle);
    });

    return () => unsubscribe();
  }, [lng, ltd, radiusSpring]);

  const renderDistanceController = (
    <div
      style={{
        transition: 'all',
        transitionDuration: '400ms',
      }}
      className="absolute top-3 bottom-[10%] left-3 flex flex-col gap-2 opacity-25 hover:opacity-100"
    >
      <Card className="py-1 px-2 w-full rounded-sm">
        <Typography
          className="pointer-events-none selection:pointer-events-none"
          fontWeight={600}
          variant="body2"
        >
          {`${rad.get()} km`}
        </Typography>
      </Card>

      {/* <Card className='hover:opacity-100 flex items-center justify-center py-3 w-full max-w-32 h-[70%] rounded-sm'> */}
      <Slider
        className="w-12 cursor-pointer"
        onValueChange={(val: any) => {
          const freshVal = val[0];
          rad.set(freshVal / 1000);
        }}
        step={80}
        defaultValue={[defaultRadius * 1000]}
        max={10000}
        min={2000}
      />
      {/* </Card> */}
    </div>
  );

  const renderTitle = (
    <Card className="absolute top-4 left-1/2 py-1 px-2 -translate-x-[50%] rounded-md">
      <Typography
        className="pointer-events-none selection:pointer-events-none"
        fontWeight={600}
        variant="body2"
      >
        Delivery Area
      </Typography>
    </Card>
  );

  return (
    <div className="rounded-lg relative overflow-hidden w-full h-full">
      {currentView === 'map' && (
        <Map
          mapboxAccessToken={import.meta.env.VITE_REACT_APP_MAPBOX_ACCESS_TOKEN}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          mapLib={import('mapbox-gl')}
          initialViewState={{
            longitude: lng,
            latitude: ltd,
            zoom: 10,
          }}
          style={{
            height: '100%',
          }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
        >
          <NavigationControl style={{ transform: 'translate(0px, 64px)' }} />

          {renderTitle}
          {renderDistanceController}
          <Source id="my-data" type="geojson" data={scaledCircle}>
            <Layer
              id="point-90-hi"
              type="fill"
              paint={{
                'fill-color': '#02006D',
                'fill-opacity': 0.08,
                'fill-outline-color': '#02006D',
              }}
            />
          </Source>
          <Marker latitude={ltd} longitude={lng}>
            <MapPin
              color="#070072"
              fill="#edecfa"
              className="-translate-y-[50%]"
            />
          </Marker>
        </Map>
      )}

      {currentView === 'list' && (
        <Card className="h-full p-3 relative  flex-1">
          <div className="flex justify-center items-center flex-row mb-12">
            <Car size={20} className="mr-[3px] text-[#212B36]" />
            <Typography
              className="pointer-events-none selection:pointer-events-none"
              fontWeight={600}
              variant="body2"
            >
              Delivery Coverage
            </Typography>
          </div>
          <div className="flex flex-col max-h-[310px] overflow-y-scroll">
            {deliveryAreas?.map((item: string, i: number) => (
              <div
                key={i}
                className={`px-2 py-2 ${i % 2 === 0 ? 'bg-[#F4F4FC]' : ''}`}
              >
                <Typography variant="body2">{item}</Typography>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
